(() => {
  if (window.RRDrawerLayout) return;

  const root = document.documentElement;
  const drawerSelector = '#facets-drawer, country-drawer.country-selector-drawer, cart-drawer, header-sidebar#sidebar-menu[data-rr-navigation-drawer]';
  const desktop = window.matchMedia('(min-width: 1000px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const openDrawers = new Map();
  const nonModalDrawers = new Map();
  const managedDrawers = new WeakMap();
  const closingDrawers = new Set();
  const pageInertStates = new Map();
  const activeTriggers = new Map();
  const productEdges = new Set();
  let desiredDrawer;
  let requestRevision = 0;
  let headerWidth = 0;
  let reservationSide = 'right';
  let overlayDrawer;
  let backdrop;
  let sequence = 0;
  let frame = 0;
  let watching = false;
  let panelObserver;
  let headerObserver;
  let treeObserver;
  let observedPanel;
  let navigation;
  let logo;
  let reservedWidth = 0;
  let reservationDuration = 300;
  let scrollFrame = 0;
  let scrollGuard;
  let readingAnchor;
  let hoverTimer;
  let hoverMenu;
  const cancelHoverTimer = () => {
    clearTimeout(hoverTimer);
    hoverTimer = null;
  };


  const scrollKeys = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab']);
  const stopScrollGuard = () => {
    if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
    scrollFrame = 0;
    scrollGuard = null;
    root.classList.remove('rr-drawer-scroll-settling');
    if (!readingAnchor) {
      window.removeEventListener('wheel', invalidateReadingAnchor, true);
      window.removeEventListener('touchstart', invalidateReadingAnchor, true);
      window.removeEventListener('pointerdown', invalidateReadingAnchor, true);
      window.removeEventListener('keydown', handleScrollIntent, true);
    }
  };

  function invalidateReadingAnchor(event) {
    if (event?.composedPath?.().some((element) => isDrawer(element)
      || (event.type === 'pointerdown' && element.matches?.('.shopify-section--header')))) return;
    readingAnchor = null;
    stopScrollGuard();
  }

  function handleScrollIntent(event) {
    if (scrollKeys.has(event.key)) invalidateReadingAnchor(event);
  }

  const holdScrollAnchor = () => {
    if (!scrollGuard) return;
    const { anchor, top, y, x } = scrollGuard;
    if (anchor && !anchor.isConnected) {
      invalidateReadingAnchor();
      return;
    }
    const nextY = anchor?.isConnected
      ? window.scrollY + anchor.getBoundingClientRect().top - top : y;
    const maxY = Math.max(0, root.scrollHeight - window.innerHeight);
    const target = Math.max(0, Math.min(nextY, maxY));
    if (Math.abs(window.scrollY - target) > 0.1) {
      window.scrollTo({ top: target, left: x, behavior: 'instant' });
    }
  };

  const keepScrollAnchor = (time) => {
    scrollFrame = 0;
    if (!scrollGuard) return;
    holdScrollAnchor();
    if (!scrollGuard) return;
    if (time >= scrollGuard.until) {
      if (reservedWidth === 0) readingAnchor = null;
      stopScrollGuard();
    }
    else scrollFrame = window.requestAnimationFrame(keepScrollAnchor);
  };

  const beginScrollGuard = (duration) => {
    if (readingAnchor?.anchor && !readingAnchor.anchor.isConnected) readingAnchor = null;
    stopScrollGuard();
    if (window.scrollY < 1 && !readingAnchor) return;
    const main = document.querySelector('#main');
    if (!main) {
      invalidateReadingAnchor();
      return;
    }
    const probe = Math.min(180, window.innerHeight / 4);
    let anchor = readingAnchor?.anchor?.isConnected ? readingAnchor.anchor : null;
    let score = Infinity;
    for (const element of anchor ? [] : main.querySelectorAll('.shopify-section, .product-card, .product-gallery__media, h1, h2, h3, .prose > p')) {
      if (element.closest('.product-info, [role="dialog"], [inert]')) continue;
      const box = element.getBoundingClientRect();
      if (box.height < 1 || box.width < 1 || box.bottom <= 0 || box.top >= window.innerHeight) continue;
      const distance = Math.abs(box.top - probe);
      if (distance < score) { anchor = element; score = distance; }
    }
    readingAnchor ||= {
      anchor, top: anchor?.getBoundingClientRect().top,
      x: window.scrollX, y: window.scrollY
    };
    scrollGuard = { ...readingAnchor, until: window.performance.now() + duration + 80 };
    root.classList.add('rr-drawer-scroll-settling');
    window.addEventListener('wheel', invalidateReadingAnchor, { capture: true, passive: true });
    window.addEventListener('touchstart', invalidateReadingAnchor, { capture: true, passive: true });
    window.addEventListener('pointerdown', invalidateReadingAnchor, { capture: true, passive: true });
    window.addEventListener('keydown', handleScrollIntent, true);
    scrollFrame = window.requestAnimationFrame(keepScrollAnchor);
  };

  const isDrawer = (element) => element?.matches?.(drawerSelector);
  const drawerSide = (drawer) => drawer?.getAttribute('open-from') === 'left' ? 'left' : 'right';
  const isNonModalDrawer = (drawer) => isDrawer(drawer) && drawer.id !== 'facets-drawer'
    && (drawer.id !== 'sidebar-menu' || desktop.matches);
  const isModalOpen = () => Boolean(document.querySelector('[open][aria-modal="true"], dialog:modal'));
  const focusedElement = () => {
    let element = document.activeElement;
    while (element?.shadowRoot?.activeElement) element = element.shadowRoot.activeElement;
    return element;
  };
  const containsFocus = (drawer, element) => {
    for (let node = element; node; node = node.getRootNode?.().host) {
      if (node === drawer || drawer.contains?.(node) || drawer.shadowRoot?.contains?.(node)) return true;
    }
    return false;
  };

  const latestDrawer = () => [...openDrawers.values()]
    .sort((a, b) => b.order - a.order)[0]?.drawer;

  const focusDrawer = (drawer) => {
    const menuTrigger = desktop.matches && drawer.id === 'sidebar-menu'
      ? [...(drawer.controls || [])].find((control) => control.closest('.header__primary-nav')) : null;
    const target = menuTrigger || drawer.querySelector('[data-country-search-input]')
      || drawer.shadowRoot?.querySelector('[part~="close-button"]') || drawer;
    target.focus?.({ preventScroll: true });
  };

  const setPageOverlay = (drawer, edgeDrawer = drawer) => {
    overlayDrawer = drawer;
    const product = Boolean(drawer && document.querySelector('.shopify-section--main-product'));
    const edgePanel = edgeDrawer?.shadowRoot?.querySelector('[part~="content"]');
    const edgePanelWidth = edgePanel?.getBoundingClientRect().width || 0;
    const attachedEdge = Boolean(edgeDrawer && edgePanelWidth > 0
      && edgePanelWidth < (root.clientWidth || window.innerWidth) - 1);
    root.classList.toggle('rr-drawer-attached-edge', attachedEdge);
    root.classList.toggle('rr-drawer-product-overlay', product);
    for (const previous of productEdges) {
      const opposingExit = managedDrawers.get(previous)?.closing && drawerSide(previous) !== drawerSide(edgeDrawer);
      if ((previous !== edgeDrawer && !opposingExit) || (!attachedEdge && !(opposingExit && edgeDrawer))) {
        previous.classList.remove('rr-drawer-product-edge');
        previous.classList.remove('rr-drawer-navigation-edge');
        productEdges.delete(previous);
      }
    }
    if (attachedEdge) {
      const panel = edgePanel;
      const state = managedDrawers.get(edgeDrawer);
      if (panel && state && !state.edge) {
        state.edge = document.createElement('div');
        state.edge.setAttribute('part', 'rr-edge-vignette');
        state.edge.setAttribute('aria-hidden', 'true');
        panel.appendChild(state.edge);
      }
      edgeDrawer.classList.add('rr-drawer-product-edge');
      edgeDrawer.classList.toggle('rr-drawer-navigation-edge', !document.querySelector('.shopify-section--main-product'));
      productEdges.add(edgeDrawer);
    }
    if (drawer) {
      if (!backdrop) {
        backdrop = document.createElement('div');
        backdrop.id = 'rr-drawer-backdrop';
        backdrop.setAttribute('aria-hidden', 'true');
        backdrop.addEventListener('click', () => overlayDrawer?.hide());
        document.body.appendChild(backdrop);
        backdrop.getBoundingClientRect();
      }
      for (const element of document.querySelectorAll('#main, .shopify-section-group-footer-group, .rr-scroll-cue')) {
        if (!pageInertStates.has(element)) pageInertStates.set(element, element.inert);
        element.inert = true;
      }
    } else {
      for (const [element, inert] of pageInertStates) element.inert = inert;
      pageInertStates.clear();
    }
    backdrop?.classList.toggle('is-visible', Boolean(drawer?.hasAttribute('open')));
    root.classList.toggle('rr-drawer-overlay-open', Boolean(drawer));
  };

  const updateTriggers = (drawer) => {
    const related = drawer?.matches('country-drawer')
      ? [...document.querySelectorAll('country-drawer.country-selector-drawer')] : [drawer];
    const controls = new Set(isNonModalDrawer(drawer)
      ? related.flatMap((element) => element.controls || []).filter((control) => control.closest('.header__secondary-nav, .header__primary-nav')) : []);
    for (const [control, target] of activeTriggers) {
      if (controls.has(control)) continue;
      control.classList.remove('rr-drawer-trigger-active');
      if (target !== null) control.setAttribute('aria-controls', target);
      control.setAttribute('aria-expanded', String(Boolean(document.getElementById(target)?.hasAttribute('open'))));
      activeTriggers.delete(control);
    }
    for (const control of controls) {
      if (!activeTriggers.has(control)) activeTriggers.set(control, control.getAttribute('aria-controls'));
      control.setAttribute('aria-controls', drawer.id);
      control.setAttribute('aria-expanded', String(drawer.hasAttribute('open')));
      control.classList.add('rr-drawer-trigger-active');
    }
  };

  const preserveCountryState = (drawer) => {
    const state = managedDrawers.get(drawer);
    const search = drawer.querySelector('country-search');
    if (!state || !search?.input) return;
    state.countrySearch = { value: search.input.value };
  };

  const restoreCountryState = (drawer, onlyIfUnchanged = false) => {
    const saved = managedDrawers.get(drawer)?.countrySearch;
    const search = drawer.querySelector('country-search');
    if (!saved || !search?.input) return;
    // Opening focus may require a second scroll repair, but must not replace
    // a query the shopper has already edited while the panel was entering.
    if (onlyIfUnchanged && search.input.value !== saved.value) return;
    search.cancelMotion();
    search.input.value = saved.value;
    search.applyQuery(search.normalize(saved.value));
    const scroller = search.getScrollContainer?.();
    // A closed country drawer always reopens at the beginning, even on handoff.
    if (scroller) scroller.scrollTop = 0;
    search.updateBottomVignette();
    search.ensureVisibleFocus?.();
  };

  const overrideAnimation = (drawer, entering, create) => {
    const property = entering ? 'createEnterAnimationControls' : 'createLeaveAnimationControls';
    const descriptor = Object.getOwnPropertyDescriptor(drawer, property);
    const original = drawer[property];
    if (typeof original !== 'function') return;
    drawer[property] = function () {
      if (descriptor) Object.defineProperty(drawer, property, descriptor);
      else delete drawer[property];
      const panel = this.shadowRoot?.querySelector('[part~="content"]');
      if (!panel?.animate) return original.call(this);
      return create(panel);
    };
  };

  const runPanelAnimation = (drawer, panel, frames, options, entering, fadeContent = false) => {
    const managed = managedDrawers.get(drawer);
    const previousMotion = managed?.panelMotion;
    const parts = ['header', 'body', 'footer'].map((part) => drawer.shadowRoot?.querySelector(`[part~="${part}"]`)).filter(Boolean);
    const needsFade = fadeContent || Boolean(previousMotion?.fading && !entering);
    const previousFades = needsFade && !entering
      ? parts.map((part) => window.getComputedStyle(part).filter || 'opacity(1)') : [];
    previousMotion?.animations?.forEach((animation) => animation.cancel());
    for (const animation of panel.getAnimations?.() || []) animation.cancel();
    panel.style.transform = 'translateX(0)';
    panel.style.opacity = '1';
    const motion = {};
    if (managed) managed.panelMotion = motion;
    const animations = [panel.animate(frames, { ...options, fill: 'both' })];
    motion.animations = animations;
    if (needsFade) {
      motion.fading = true;
      const duration = options.duration / (fadeContent ? 2 : 1);
      const fades = parts.map((part, index) => part.animate(
        [{ filter: entering ? 'opacity(0)' : previousFades[index] }, { filter: entering ? 'opacity(1)' : 'opacity(0)' }],
        { duration, delay: entering ? duration : 0, easing: 'ease-in-out', fill: 'both' }
      ));
      animations.push(...fades);
      if (!entering && fadeContent) Promise.all(fades.map((animation) => animation.finished.catch(() => {}))).then(() => {
        if (managed?.panelMotion === motion) drawer.classList.add('rr-drawer-handoff-obsolete');
      });
    }
    const finished = Promise.all(animations.map((animation) => animation.finished.catch(() => {}))).then(() => {
      animations.forEach((animation) => animation.cancel());
      if (managed?.panelMotion === motion) {
        delete managed.panelMotion;
        if (entering) {
          delete managed.handoffWidth;
          drawer.classList.remove('rr-drawer-handoff-incoming');
        }
      }
      queueRefresh();
    });
    return { finished, finish: () => animations.forEach((animation) => animation.finish()) };
  };

  const handoffAnimation = (drawer, entering, otherWidth) => {
    const managed = managedDrawers.get(drawer);
    const pending = entering ? 'handoffEntering' : 'handoffLeaving';
    if (managed) managed[pending] = true;
    drawer.classList.toggle('rr-drawer-handoff-incoming', entering);
    drawer.classList.toggle('rr-drawer-handoff-outgoing', !entering);
    if (entering && drawer.matches('cart-drawer')) drawer.classList.add('rr-drawer-handoff-cart');
    overrideAnimation(drawer, entering, (panel) => {
      if (managed) delete managed[pending];
      const width = panel.getBoundingClientRect().width;
      const fromWidth = entering ? (otherWidth || width) : width;
      const toWidth = entering ? width : (otherWidth || width);
      if (entering && managed) managed.handoffWidth = width;
      panel.style.marginInlineStart = drawerSide(drawer) === 'left' ? '0' : 'auto';
      const duration = reducedMotion.matches ? 0 : reservationDuration;
      return runPanelAnimation(drawer, panel, [
        { width: `${fromWidth}px`, maxWidth: `${fromWidth}px` },
        { width: `${toWidth}px`, maxWidth: `${toWidth}px` }
      ], { duration, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)' }, entering, true);
    });
  };

  const productSlideAnimation = (drawer, entering, handoff = false) => {
    const managed = managedDrawers.get(drawer);
    const pending = entering ? 'handoffEntering' : 'handoffLeaving';
    if (handoff && managed) managed[pending] = true;
    overrideAnimation(drawer, entering, (panel) => {
      if (handoff && managed) delete managed[pending];
      const sign = drawerSide(drawer) === 'left' ? -1 : 1;
      panel.style.marginInlineStart = sign < 0 ? '0' : 'auto';
      const edgeWidth = managedDrawers.get(drawer)?.edge?.getBoundingClientRect().width || 0;
      const travel = `translateX(${sign * (panel.getBoundingClientRect().width + edgeWidth)}px)`;
      const current = window.getComputedStyle(panel).transform;
      return runPanelAnimation(drawer, panel, [
        { transform: entering ? travel : (current && current !== 'none' ? current : 'translateX(0)') },
        { transform: entering ? 'translateX(0)' : travel }
      ], { duration: reducedMotion.matches ? 0 : 300, easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)' }, entering);
    });
  };

  const installDrawer = (drawer) => {
    if (!isDrawer(drawer) || managedDrawers.has(drawer) || typeof drawer.show !== 'function') return;
    const state = { show: drawer.show, hide: drawer.hide };
    managedDrawers.set(drawer, state);
    drawer.show = (animate = true) => show(drawer, animate);
    drawer.hide = () => hide(drawer);
  };

  async function show(drawer, animate = true) {
    installDrawer(drawer);
    const state = managedDrawers.get(drawer);
    if (!state || !drawer.isConnected) return;
    const revision = ++requestRevision;
    desiredDrawer = drawer;
    if (state.closing) await state.closing;
    if (revision !== requestRevision || !drawer.isConnected) return;
    for (const surface of document.querySelectorAll('header-search[open]')) {
      surface.focusTrap?.deactivate({ returnFocus: false, onDeactivate: () => {} });
      await surface.hide();
      if (revision !== requestRevision) return;
    }
    const outgoing = latestDrawer();
    if (outgoing === drawer && drawer.hasAttribute('open')) return;
    if (outgoing && desktop.matches) {
      if (drawerSide(outgoing) !== drawerSide(drawer)) productSlideAnimation(drawer, true, true);
      else {
        for (const closing of closingDrawers) {
          if (closing === outgoing || drawerSide(closing) !== drawerSide(drawer)) continue;
          closing.classList.add('rr-drawer-handoff-obsolete');
          managedDrawers.get(closing)?.panelMotion?.animations?.forEach((animation) => animation.finish());
        }
        handoffAnimation(drawer, true, outgoing.shadowRoot?.querySelector('[part~="content"]')?.getBoundingClientRect().width || headerWidth);
      }
    }
    return state.show.call(drawer, animate);
  }

  function hide(drawer) {
    installDrawer(drawer);
    const state = managedDrawers.get(drawer);
    if (!state) return Promise.resolve();
    if (!state.internalHide) {
      desiredDrawer = null;
      ++requestRevision;
    }
    state.internalHide = false;
    if (state.closing) return state.closing;
    return state.hide.call(drawer);
  }

  const finishClosingDrawer = (drawer) => {
    const managed = managedDrawers.get(drawer);
    if (!managed) return;
    managed.panelMotion?.animations?.forEach((animation) => animation.cancel());
    delete managed.panelMotion;
    delete managed.handoffWidth;
    drawer.inert = managed.inert ?? false;
    drawer.classList.remove('rr-drawer-departing', 'rr-drawer-handoff-incoming', 'rr-drawer-handoff-outgoing', 'rr-drawer-handoff-obsolete', 'rr-drawer-handoff-cart');
    closingDrawers.delete(drawer);
    managed.closing = null;
    managed.finishClosing?.();
    managed.finishClosing = null;
  };

  const restoreNonModalProperties = (drawer) => {
    const state = nonModalDrawers.get(drawer);
    if (!state) return;
    for (const property of ['shouldLock', 'shouldTrapFocus']) {
      if (state[property]) Object.defineProperty(drawer, property, state[property]);
      else delete drawer[property];
    }
    if (state.ariaModal === null) drawer.removeAttribute('aria-modal');
    else drawer.setAttribute('aria-modal', state.ariaModal);
    nonModalDrawers.delete(drawer);
  };

  const prepareNonModal = (drawer) => {
    if (!isNonModalDrawer(drawer) || nonModalDrawers.has(drawer)) return;
    const active = focusedElement();
    nonModalDrawers.set(drawer, {
      shouldLock: Object.getOwnPropertyDescriptor(drawer, 'shouldLock'),
      shouldTrapFocus: Object.getOwnPropertyDescriptor(drawer, 'shouldTrapFocus'),
      ariaModal: drawer.getAttribute('aria-modal'),
      opener: managedDrawers.get(drawer)?.pendingOpener || (drawer.controls?.includes(active) ? active : null)
    });
    Object.defineProperties(drawer, {
      shouldLock: { configurable: true, get: () => false },
      shouldTrapFocus: { configurable: true, get: () => false }
    });
    drawer.setAttribute('aria-modal', 'false');
    const managed = managedDrawers.get(drawer);
    if (managed) managed.pendingOpener = null;
  };

  const rememberOpenDrawer = (drawer) => {
    if (isDrawer(drawer) && drawer.isConnected && drawer.hasAttribute('open') && !openDrawers.has(drawer)) {
      openDrawers.set(drawer, { drawer, order: ++sequence });
    }
  };

  const getHeaderRestingBounds = (element) => {
    const bounds = element.getBoundingClientRect();
    let top = bounds.top;
    let left = bounds.left;
    // The approved push layout neutralizes the two header hide transforms.
    // Capture their resting position so opening from a scrolled footer does
    // not pin the logo to a stale, negative translated coordinate.
    for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
      if (!ancestor.matches('.header, .shopify-section--header')) continue;
      const transform = window.getComputedStyle(ancestor).transform;
      if (transform && transform !== 'none' && window.DOMMatrixReadOnly) {
        const matrix = new window.DOMMatrixReadOnly(transform);
        top -= matrix.m42;
        left -= matrix.m41;
      }
    }
    return { top, left };
  };

  const alignDrawerHeader = (headerOpen, capture = false) => {
    if (!headerOpen) {
      root.style.removeProperty('--rr-drawer-header-center-y');
      return;
    }
    const nextNavigation = document.querySelector('button[aria-controls="sidebar-menu"]');
    const nextLogo = document.querySelector('.shopify-section--header .header__logo')
      || document.querySelector('.header__logo');
    const row = document.querySelector('.shopify-section--header .header__secondary-nav')
      || document.querySelector('.header__secondary-nav');
    // Read all resting geometry before writing inherited variables or fixed classes.
    // Interleaving these operations recalculates styles across the entire storefront.
    const navBounds = capture && nextNavigation ? getHeaderRestingBounds(nextNavigation) : null;
    const logoBounds = capture && nextLogo ? getHeaderRestingBounds(nextLogo) : null;
    const rowBounds = row ? getHeaderRestingBounds(row) : null;
    const rowHeight = row?.getBoundingClientRect().height || 0;
    const center = rowBounds ? Math.max(0, rowBounds.top + rowHeight / 2) : null;
    const logoHeight = desktop.matches ? nextLogo?.getBoundingClientRect().height : 0;
    const navigationHeight = desktop.matches ? nextNavigation?.getBoundingClientRect().height : 0;

    if (capture) {
      if (navigation !== nextNavigation) navigation?.classList.remove('rr-drawer-fixed-navigation');
      if (logo !== nextLogo) logo?.classList.remove('rr-drawer-fixed-logo');
      navigation = nextNavigation;
      logo = nextLogo;
      if (navigation && navBounds) {
        navigation.classList.add('rr-drawer-fixed-navigation');
        root.style.setProperty('--rr-drawer-navigation-top', `${Math.max(0, navBounds.top)}px`);
        root.style.setProperty('--rr-drawer-navigation-left', `${Math.max(0, navBounds.left)}px`);
      }
      if (logo && logoBounds) {
        logo.classList.add('rr-drawer-fixed-logo');
        root.style.setProperty('--rr-drawer-logo-top', `${Math.max(0, logoBounds.top)}px`);
      }
    }
    if (center === null) return;
    root.style.setProperty('--rr-drawer-header-center-y', `${center}px`);
    if (desktop.matches) {
      for (const [height, property] of [[logoHeight, '--rr-drawer-logo-top'], [navigationHeight, '--rr-drawer-navigation-top']]) {
        if (!(height > 0)) continue;
        const value = `${Math.max(0, center - height / 2)}px`;
        if (root.style.getPropertyValue(property) !== value) root.style.setProperty(property, value);
      }
    }
  };

  const shouldStackHeader = (drawer, panelWidth, viewportWidth) => {
    if (!desktop.matches) return false;
    const headerLogo = document.querySelector('.shopify-section--header .header__logo')
      || document.querySelector('.header__logo');
    const row = document.querySelector('.shopify-section--header .header__secondary-nav')
      || document.querySelector('.header__secondary-nav');
    const primary = document.querySelector('button[aria-controls="sidebar-menu"]');
    const logoWidth = headerLogo?.getBoundingClientRect().width || 0;
    const rowBounds = row?.getBoundingClientRect();
    if (!logoWidth || !rowBounds?.width) return drawerSide(drawer) !== 'left' && viewportWidth - panelWidth < 800;
    const rowTranslate = Number.parseFloat(window.getComputedStyle(row).translate) || 0;
    const rowLeft = getHeaderRestingBounds(row).left - rowTranslate;
    const projectedLeft = rowLeft - (drawerSide(drawer) === 'right' ? panelWidth : 0);
    // The logo is pinned at 50vw, which includes a classic scrollbar gutter.
    const logoShift = drawerSide(drawer) === 'right' ? -panelWidth / 2 : panelWidth / 2;
    const centeredLeft = ((window.innerWidth || viewportWidth) - logoWidth) / 2 + logoShift;
    const centeredRight = centeredLeft + logoWidth;
    const primaryBounds = primary?.getBoundingClientRect();
    const primaryRight = primary && primaryBounds?.width
      ? getHeaderRestingBounds(primary).left + primaryBounds.width : 0;
    return projectedLeft < centeredRight + 24 || primaryRight + 24 > centeredLeft;
  };

  const applyReservation = (width, duration, nextHeaderWidth = width, headerOpen = nextHeaderWidth > 0, side = reservationSide) => {
    const sideChanged = side !== reservationSide;
    const changed = width !== reservedWidth || sideChanged;
    if (changed) beginScrollGuard(duration);
    // Restore the viewport-owned sticky header before measuring an opening
    // from deep scroll; body-owned overflow would report negative bounds.
    const wasHeaderOpen = root.classList.contains('rr-drawer-header-open');
    root.classList.toggle('rr-drawer-header-open', headerOpen);
    alignDrawerHeader(headerOpen, desktop.matches && headerOpen
      && (!wasHeaderOpen || !logo?.classList.contains('rr-drawer-fixed-logo')));

    reservedWidth = width;
    headerWidth = nextHeaderWidth;
    reservationSide = side;
    root.style.setProperty('--rr-drawer-push-duration', `${duration}ms`);
    root.style.setProperty('--rr-drawer-header-width', `${headerWidth}px`);
    root.style.setProperty('--rr-drawer-header-left', `${side === 'left' ? headerWidth : 0}px`);
    root.style.setProperty('--rr-drawer-push-left', `${side === 'left' ? width : 0}px`);
    root.classList.toggle('rr-drawer-left-open', headerOpen && side === 'left');
    if (width > 0) root.style.setProperty('--rr-drawer-push-width', `${width}px`);
    else root.style.removeProperty('--rr-drawer-push-width');
    root.classList.toggle('rr-drawer-push-open', width > 0);
    if (changed) holdScrollAnchor();

    if (!headerOpen || !desktop.matches) {
      navigation?.classList.remove('rr-drawer-fixed-navigation');
      logo?.classList.remove('rr-drawer-fixed-logo');
      root.style.removeProperty('--rr-drawer-navigation-top');
      root.style.removeProperty('--rr-drawer-navigation-left');
      root.style.removeProperty('--rr-drawer-logo-top');
    }
  };

  const stopWatching = () => {
    if (!watching) return;
    watching = false;
    window.removeEventListener('resize', queueRefresh);
    desktop.removeEventListener('change', handleDesktopChange);
    reducedMotion.removeEventListener('change', queueRefresh);
    document.removeEventListener('keydown', handleEscape);
    panelObserver?.disconnect();
    headerObserver?.disconnect();
    treeObserver?.disconnect();
    observedPanel = null;
  };

  const startWatching = () => {
    if (watching) return;
    watching = true;
    window.addEventListener('resize', queueRefresh, { passive: true });
    desktop.addEventListener('change', handleDesktopChange);
    reducedMotion.addEventListener('change', queueRefresh);
    document.addEventListener('keydown', handleEscape);
    panelObserver ||= new ResizeObserver(queueRefresh);
    headerObserver ||= new ResizeObserver(queueRefresh);
    for (const element of [document.querySelector('.shopify-section--header .header__logo'),
      document.querySelector('.shopify-section--header .header__secondary-nav')]) {
      if (element) headerObserver.observe(element);
    }
    treeObserver ||= new MutationObserver((changes) => {
      // Reparenting is complete by this microtask. A moved drawer remains
      // connected; a replaced drawer relinquishes its reservation below.
      changes.forEach((change) => change.addedNodes?.forEach((node) => {
        installDrawer(node);
        node.querySelectorAll?.(drawerSelector).forEach(installDrawer);
        rememberOpenDrawer(node);
        node.querySelectorAll?.(drawerSelector).forEach(rememberOpenDrawer);
      }));
      if (changes.some((change) => change.type === 'childList' || isDrawer(change.target))) {
        queueRefresh();
      }
    });
    treeObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open']
    });
  };

  const reconcile = () => {
    for (const drawer of closingDrawers) {
      if (!drawer.isConnected) finishClosingDrawer(drawer);
    }
    for (const [drawer] of openDrawers) {
      if (!drawer.isConnected || !drawer.hasAttribute('open')) openDrawers.delete(drawer);
    }
    for (const [drawer] of nonModalDrawers) {
      if (!drawer.isConnected) restoreNonModalProperties(drawer);
    }

    // The newest open drawer owns the entire reservation. Never add widths,
    // and never reserve an older drawer's width behind a newer drawer.
    let active;
    for (const entry of openDrawers.values()) {
      if (!active || entry.order > active.order) active = entry;
    }

    if (!active) {
      const departing = overlayDrawer?.isConnected && managedDrawers.get(overlayDrawer)?.closing ? overlayDrawer : null;
      const closing = [...closingDrawers].at(-1);
      setPageOverlay(departing);
      updateTriggers(closing);
      applyReservation(0, reducedMotion.matches ? 0 : reservationDuration, 0,
        Boolean(closing) && !(closing.id === 'sidebar-menu' && !desktop.matches));
      if (!closing) root.classList.remove('rr-drawer-header-stacked');
      if (closingDrawers.size) startWatching();
      else stopWatching();
      return;
    }

    startWatching();
    const drawer = active.drawer;
    const panel = drawer.shadowRoot?.querySelector('[part~="content"]');
    if (panel !== observedPanel) {
      panelObserver.disconnect();
      observedPanel = panel;
      if (panel) panelObserver.observe(panel);
    }

    const width = Math.ceil(managedDrawers.get(drawer)?.handoffWidth || panel?.getBoundingClientRect().width || 0);
    const viewportWidth = root.clientWidth || window.innerWidth;
    const isFacet = drawer.id === 'facets-drawer';
    const isProduct = Boolean(document.querySelector('.shopify-section--main-product'));
    // The two-column menu needs more room than utility drawers; do not
    // compress collection headings/cards into a narrow third column.
    const minimumPageWidth = drawer.id === 'sidebar-menu' ? 800 : 600;
    const overlay = !isFacet && (isProduct || !desktop.matches || viewportWidth - width < minimumPageWidth);
    const overlayTarget = overlay && !(drawer.id === 'sidebar-menu' && !desktop.matches) ? drawer : null;
    setPageOverlay(overlayTarget, overlayTarget || ((isFacet && !desktop.matches) || drawer.matches('#sidebar-menu, country-drawer, cart-drawer') ? drawer : null));
    updateTriggers(drawer);
    const canReserve = desktop.matches && width > 0 && !overlay
      && (isFacet || viewportWidth - width >= minimumPageWidth);
    reservationDuration = 300;
    const duration = drawer.getAttribute('open') === 'immediate' || reducedMotion.matches
      ? 0 : reservationDuration;
    const stackedHeader = shouldStackHeader(drawer, width, viewportWidth);
    root.style.setProperty('--rr-drawer-panel-width', `${width}px`);
    root.classList.toggle('rr-drawer-header-stacked', stackedHeader);
    applyReservation(canReserve ? width : 0, duration, desktop.matches && !stackedHeader ? width : 0,
      drawer.id !== 'sidebar-menu' || desktop.matches, drawerSide(drawer));
  };

  function queueRefresh() {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      reconcile();
    });
  }

  function handleDesktopChange() {
    // Do not change a native mobile modal's lock ownership mid-lifecycle.
    const menu = document.querySelector('header-sidebar#sidebar-menu[open]');
    if (menu) menu.hide();
    queueRefresh();
  }

  const refresh = (drawer) => {
    if (isDrawer(drawer)) {
      installDrawer(drawer);
      if (drawer.isConnected && drawer.hasAttribute('open')) {
        rememberOpenDrawer(drawer);
      } else openDrawers.delete(drawer);
    } else {
      // On a rare hot load, an already open native modal keeps its current
      // lock lifecycle until it closes. Its next before-show is nonmodal;
      // changing shouldLock mid-open would corrupt the native layer count.
      document.querySelectorAll(drawerSelector).forEach((element) => {
        installDrawer(element);
        rememberOpenDrawer(element);
      });
    }

    reconcile();
    // Country width can settle in its own before-show handler and drawers
    // append to body after the capture-phase event. Measure once more then.
    if (openDrawers.size) queueRefresh();
  };

  const forget = (drawer) => {
    openDrawers.delete(drawer);
    reconcile();
  };

  const handleDialog = (event) => {
    const drawer = event.target;
    // The menu's second column is a nested native dialog, not a competing
    // drawer. Its own trap must not steal focus from the interactive header.
    if (event.type === 'dialog:before-show' && drawer.matches?.('header-sidebar-collapsible-panel')
      && isDrawer(drawer.closest('header-sidebar'))) {
      Object.defineProperty(drawer, 'shouldTrapFocus', { configurable: true, get: () => false });
      drawer.focusTrap?.deactivate({ returnFocus: false, onDeactivate: () => {} });
      drawer.setAttribute('aria-modal', 'false');
      return;
    }
    if (!isDrawer(drawer)) {
      if (event.type === 'dialog:before-show' && drawer.matches?.('header-search')) {
        desiredDrawer = null;
        ++requestRevision;
        for (const open of [...openDrawers.keys()]) {
          const modalState = nonModalDrawers.get(open);
          if (modalState) modalState.suppressReturn = true;
          open.focusTrap?.deactivate({ returnFocus: false, onDeactivate: () => {} });
          open.hide();
        }
        setPageOverlay(null);
      }
      return;
    }
    installDrawer(drawer);
    const managed = managedDrawers.get(drawer);
    const closingFocus = event.type === 'dialog:before-hide' ? focusedElement() : null;
    if (event.type === 'dialog:before-show') {
      cancelHoverTimer();
      managed?.stopCascade?.();
      managed?.cascadeAnimations?.forEach(animation => animation.cancel());
      if (!reducedMotion.matches && (drawer.getAttribute('open') !== 'immediate' || drawer.id === 'facets-drawer')) {
        const filterDrawer = drawer.id === 'facets-drawer';
        const cartDrawer = drawer.matches('cart-drawer');
        const countryDrawer = drawer.matches('country-drawer');
        const navDrawer = drawer.id === 'sidebar-menu';
        const closeButton = filterDrawer || cartDrawer || countryDrawer ? drawer.shadowRoot?.querySelector('[part~="close-button"]') : null;
        if (cartDrawer) drawer.classList.add('rr-component-cascade');
        const startCascade = () => {
        if (!drawer.hasAttribute('open')) return;
        const items = drawer.matches('country-drawer')
          ? drawer.querySelectorAll('[slot="header"], .country-search__field, [role="option"], [slot="footer"]')
          : filterDrawer ? drawer.querySelectorAll('[slot="header"], .rr-drawer-sort__summary, .rr-drawer-sort__options > *, .accordion-list > :not(.facets-filter-group), .facets-filter-group > *, [slot="footer"]')
          : cartDrawer ? drawer.querySelectorAll('[slot="header"], :scope > free-shipping-bar, .cart-drawer__items > .line-item, .cart-drawer__empty-state > *, .cart-drawer__complementary-products > *, [slot="footer"]')
          : navDrawer ? drawer.querySelectorAll('.header-sidebar__main-panel a, .header-sidebar__main-panel summary, .header-sidebar__main-panel img') : [];
        const scroller = drawer.shadowRoot?.querySelector('[part~="body"]');
        const visibleOnly = filterDrawer || cartDrawer || countryDrawer || navDrawer;
        const bodyBounds = scroller?.getBoundingClientRect();
        const footerBounds = drawer.querySelector('[slot="footer"]')?.getBoundingClientRect();
        const contentBottom = cartDrawer && footerBounds?.height > 0 && footerBounds.top > (bodyBounds?.top || 0)
          ? Math.min(bodyBounds?.bottom ?? window.innerHeight, footerBounds.top)
          : bodyBounds?.bottom ?? window.innerHeight;
        const candidates = closeButton ? [closeButton, ...items] : [...items];
        const targets = candidates.filter(item => {
          if (filterDrawer) {
            // Closed details retain measurable descendants; they must not consume stagger slots.
            const closed = item.closest?.('details:not([open])');
            if (closed && !item.matches?.('summary')) return false;
            // Availability includes a nested accordion list: animate its visible wrapper only.
            if (candidates.some(parent => parent !== item && parent.contains?.(item))) return false;
          }
          if (typeof item.animate !== 'function') return false;
          if (!visibleOnly) return true;
          const bounds = item.getBoundingClientRect();
          const chrome = item === closeButton || item.matches('[slot="header"], [slot="footer"]');
          const top = chrome ? 0 : Math.max(0, bodyBounds?.top || 0);
          const bottom = chrome ? window.innerHeight : Math.min(window.innerHeight, contentBottom);
          return bounds.width > 0 && bounds.height > 0 && bounds.bottom > top && bounds.top < bottom;
        });
        managed.cascadeAnimations = targets.map((item, index) => item.animate(
          item === closeButton || item.matches?.('[slot="header"]')
            ? [{ filter: 'opacity(0)' }, { filter: 'opacity(1)' }]
            : [{ filter: 'opacity(0)', transform: 'translateY(6px)' }, { filter: 'opacity(1)', transform: 'translateY(0)' }],
          { duration: filterDrawer ? 280 : 240, delay: filterDrawer ? 100 + index * 26 : 180 + index * 24, easing: 'ease-out', fill: 'backwards' }
        ));
        if (visibleOnly && scroller) {
          const initialScroll = scroller.scrollTop;
          const finish = () => managed.stopCascade?.();
          const onScroll = () => { if (scroller.scrollTop !== initialScroll) finish(); };
          const onKey = event => { if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) finish(); };
          managed.stopCascade = () => {
            managed.cascadeAnimations?.forEach(animation => animation.cancel());
            scroller.removeEventListener('scroll', onScroll);
            drawer.removeEventListener('wheel', finish, true);
            drawer.removeEventListener('touchmove', finish, true);
            drawer.removeEventListener('keydown', onKey, true);
            delete managed.stopCascade;
          };
          scroller.addEventListener('scroll', onScroll, { passive: true });
          drawer.addEventListener('wheel', finish, { passive: true, capture: true });
          drawer.addEventListener('touchmove', finish, { passive: true, capture: true });
          drawer.addEventListener('keydown', onKey, true);
        }
        };
        if (cartDrawer || countryDrawer || navDrawer) window.queueMicrotask(() => {
          if (navDrawer) drawer.revealItems?.();
          restoreCountryState(drawer);
          startCascade();
        });
        else startCascade();
      }
      prepareNonModal(drawer);
      if (!managed?.handoffEntering && isNonModalDrawer(drawer) && desktop.matches
        && (document.querySelector('.shopify-section--main-product') || (drawer.id === 'sidebar-menu'
          && (root.clientWidth || window.innerWidth) - (drawer.shadowRoot?.querySelector('[part~="content"]')?.getBoundingClientRect().width || 0) < 800))) productSlideAnimation(drawer, true);
      const outgoing = [...openDrawers.keys()].filter((element) => element !== drawer && element.hasAttribute('open'));
      openDrawers.set(drawer, { drawer, order: ++sequence });
      desiredDrawer = drawer;
      for (const element of outgoing) {
        const oldState = nonModalDrawers.get(element);
        if (oldState) oldState.suppressReturn = true;
        const outgoingState = managedDrawers.get(element);
        if (outgoingState) outgoingState.internalHide = true;
        if (desktop.matches) {
          if (drawerSide(element) !== drawerSide(drawer)) productSlideAnimation(element, false, true);
          else handoffAnimation(element, false, drawer.shadowRoot?.querySelector('[part~="content"]')?.getBoundingClientRect().width || headerWidth);
        }
        element.focusTrap?.deactivate({ returnFocus: false, onDeactivate: () => {} });
        element.hide();
      }
      window.queueMicrotask(() => {
        if (drawer.hasAttribute('open')) restoreCountryState(drawer);
      });
    }
    if (event.type === 'dialog:before-hide' && managed) {
      cancelHoverTimer();
      managed.stopCascade?.();
      managed.cascadeAnimations?.forEach(animation => animation.cancel());
      delete managed.cascadeAnimations;
      if (hoverMenu === drawer) hoverMenu = null;
      if (!managed.handoffLeaving && (drawer.classList.contains('rr-drawer-product-edge') || managed.panelMotion?.fading)) productSlideAnimation(drawer, false);
      preserveCountryState(drawer);
      managed.closing = new Promise((resolve) => { managed.finishClosing = resolve; });
      closingDrawers.add(drawer);
      managed.inert = drawer.inert;
      drawer.inert = true;
      drawer.classList.add('rr-drawer-departing');
    }
    const state = nonModalDrawers.get(drawer);
    if (state) {
      if (event.type === 'dialog:before-hide') {
        state.focusAtHide = closingFocus;
        state.returnFocus = containsFocus(drawer, state.focusAtHide);
      } else if (event.type === 'dialog:after-hide' && !drawer.hasAttribute('open')) {
        const active = focusedElement();
        if (!state.suppressReturn && !latestDrawer() && state.returnFocus && state.opener?.isConnected
          && (active === state.focusAtHide || active === document.body || containsFocus(drawer, active))) {
          updateTriggers(null);
          window.queueMicrotask(() => {
            if (!desiredDrawer && !latestDrawer() && !isModalOpen() && state.opener?.isConnected) {
              state.opener.focus({ preventScroll: true });
            }
          });
        }
        restoreNonModalProperties(drawer);
      } else {
        // Base connectedCallback resets this during body reparenting; toggle
        // runs after that native lifecycle and reinstates truthful semantics.
        drawer.setAttribute('aria-modal', 'false');
        if (event.type === 'dialog:after-show' && drawer.hasAttribute('open') && latestDrawer() === drawer && drawer.matches('cart-drawer')
          && state.opener && focusedElement() === state.opener) {
          drawer.shadowRoot?.querySelector('[part~="close-button"]')?.focus({ preventScroll: true });
        }
      }
    }
    if (event.type === 'dialog:after-hide' && managed) {
      finishClosingDrawer(drawer);
    }
    if (event.type === 'dialog:after-show' && latestDrawer() === drawer && drawer.hasAttribute('open')) {
      if (overlayDrawer === drawer && !allowedFocusRegion(focusedElement())) focusDrawer(drawer);
      window.queueMicrotask(() => {
        if (latestDrawer() === drawer && drawer.hasAttribute('open')) restoreCountryState(drawer, true);
      });
    }
    refresh(drawer);
  };

  const allowedFocusRegion = (element) => {
    const header = document.querySelector('.shopify-section--header');
    return (header && containsFocus(header, element)) || (overlayDrawer && containsFocus(overlayDrawer, element));
  };

  const collectTabStops = (container) => {
    const stops = [];
    if (!container) return stops;
    if (container.shadowRoot) stops.push(...collectTabStops(container.shadowRoot));
    for (const element of container.querySelectorAll('*')) {
      if (element.shadowRoot) stops.push(...collectTabStops(element.shadowRoot));
      if (!element.matches('a[href], button, input, select, textarea, [tabindex]')) continue;
      if (element.tabIndex < 0 || element.disabled || element.closest('[inert], [hidden]')) continue;
      const style = window.getComputedStyle(element);
      if (style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0' && element.getBoundingClientRect().width > 0) stops.push(element);
    }
    return stops;
  };

  document.addEventListener('focusin', () => {
    if (overlayDrawer?.hasAttribute('open') && !isModalOpen() && !allowedFocusRegion(focusedElement())) focusDrawer(overlayDrawer);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      const menu = latestDrawer();
      if (menu?.id === 'sidebar-menu' && menu.hasAttribute('open')) {
        for (const account of menu.querySelectorAll('shopify-account')) {
          const dialog = account.shadowRoot?.querySelector('dialog[open]');
          const close = dialog?.querySelector('button.account__close');
          if (!close) continue;
          const bounds = dialog.getBoundingClientRect();
          if (bounds.width <= 0 || bounds.height <= 0) continue;
          event.preventDefault();
          event.stopImmediatePropagation();
          close.click();
          return;
        }
      }
    }
    if (event.key !== 'Tab' || !overlayDrawer?.hasAttribute('open') || isModalOpen()) return;
    const stops = [...collectTabStops(document.querySelector('.shopify-section--header')), ...collectTabStops(overlayDrawer)];
    const index = stops.indexOf(focusedElement());
    const next = event.shiftKey ? index - 1 : index + 1;
    event.preventDefault();
    (stops[(next + stops.length) % stops.length] || overlayDrawer).focus?.({ preventScroll: true });
  }, true);

  document.addEventListener('click', (event) => {
    const path = event.composedPath?.() || [event.target];
    const control = path.find((element) => element.getAttribute?.('aria-controls'));
    const drawer = control && document.getElementById(control.getAttribute('aria-controls'));
    if (isDrawer(drawer)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      installDrawer(drawer);
      cancelHoverTimer();
      if (drawer === hoverMenu && drawer.hasAttribute('open')) {
        hoverMenu = null;
        return;
      }
      hoverMenu = null;
      if (drawer.hasAttribute('open')) hide(drawer);
      else {
        const state = managedDrawers.get(drawer);
        if (state) state.pendingOpener = control;
        show(drawer);
      }
    } else if (path.some((element) => element.matches?.('shopify-account, .header__secondary-nav a[href*="/account"]'))) {
      if (path.some((element) => element.id === 'sidebar-menu' && isDrawer(element) && element.hasAttribute('open'))) return;
      desiredDrawer = null;
      ++requestRevision;
      for (const open of [...openDrawers.keys()]) {
        const state = nonModalDrawers.get(open);
        if (state) state.suppressReturn = true;
        open.focusTrap?.deactivate({ returnFocus: false, onDeactivate: () => {} });
        hide(open);
      }
      setPageOverlay(null);
    }
  }, true);

  function handleEscape(event) {
    if (event.key !== 'Escape' || event.defaultPrevented || isModalOpen()) return;
    let latest;
    for (const entry of openDrawers.values()) {
      if (!latest || entry.order > latest.order) latest = entry;
    }
    if (latest && nonModalDrawers.has(latest.drawer) && latest.drawer.hasAttribute('open')) {
      event.preventDefault();
      latest.drawer.hide();
    }
  }

  // These lifecycle events do not bubble in the base theme. Capture receives
  // them before animation starts and survives body reparenting and replacement.
  document.addEventListener('dialog:before-show', handleDialog, true);
  document.addEventListener('dialog:before-hide', handleDialog, true);
  document.addEventListener('dialog:after-show', handleDialog, true);
  document.addEventListener('dialog:after-hide', handleDialog, true);
  document.addEventListener('toggle', handleDialog, true);
  document.addEventListener('shopify:section:load', () => refresh());
  document.addEventListener('shopify:section:unload', queueRefresh);
  window.RRDrawerLayout = Object.freeze({ refresh, forget, isModalOpen, show, hide, preserveScroll: holdScrollAnchor });
  refresh();
  for (const tag of ['country-drawer', 'cart-drawer', 'facets-drawer', 'header-sidebar']) {
    window.customElements?.whenDefined(tag).then(() => refresh());
  }
  document.dispatchEvent(new CustomEvent('rr:drawer-layout-ready'));
})();
